import {
  AnalyticsEvent,
  ButtonComponentV2,
  CancelTabapayTransferInput,
  CancelTabapayTransferResultTypenames,
  Mutation,
} from '@varo/graphql-schema-types';
import { logTransactionAuthEvent } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { getTabapayErrorScreen } from 'features/myVaro/screens/ExternalAccounts/helper/TabapayHelper';
import { useEffect, useReducer } from 'react';
import CancelTabapayTransfer from 'services/graphql/mutations/MoveMoney/TabaPay/CancelTabapayTransfer';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { isCoreError } from 'shared/utils/errors/CoreError';
import { useMutation } from 'urql';

interface ErrorScreenData {
  screenType: ErrorScreenType.Error;
  messageHeadline: string;
  messageBody: string;
  primaryButton?: ButtonComponentV2;
  secondaryButton?: ButtonComponentV2;
  is3DSError?: boolean;
  processType?: string;
  analytics?: AnalyticsEvent;
}

type CancelTransferState =
  | { status: 'idle'; isLoading: false; isSuccess: false; errorScreen: null }
  | { status: 'loading'; isLoading: true; isSuccess: false; errorScreen: null }
  | { status: 'success'; isLoading: false; isSuccess: true; errorScreen: null }
  | {
      status: 'error';
      isLoading: false;
      isSuccess: false;
      errorScreen: ErrorScreenData;
    };

type CancelTransferAction =
  | { type: 'LOADING' }
  | { type: 'SUCCESS' }
  | { type: 'ERROR'; errorScreen: ErrorScreenData };

const initialState: CancelTransferState = {
  status: 'idle',
  isLoading: false,
  isSuccess: false,
  errorScreen: null,
};

const cancelTransferReducer = (
  _state: CancelTransferState,
  action: CancelTransferAction,
): CancelTransferState => {
  switch (action.type) {
    case 'LOADING':
      return {
        status: 'loading',
        isLoading: true,
        isSuccess: false,
        errorScreen: null,
      };
    case 'SUCCESS':
      return {
        status: 'success',
        isLoading: false,
        isSuccess: true,
        errorScreen: null,
      };
    case 'ERROR':
      return {
        status: 'error',
        isLoading: false,
        isSuccess: false,
        errorScreen: action.errorScreen,
      };
  }
};

interface UseCancelTabapayTransferOptions {
  onSuccess?: () => void;
}

const useCancelTabapayTransfer = (
  options?: UseCancelTabapayTransferOptions,
) => {
  const { onSuccess } = options ?? {};
  const [result, executeMutation] = useMutation<Mutation>(
    CancelTabapayTransfer,
  );
  const { segmentClient } = useAnalyticsConfiguration();
  const [state, dispatch] = useReducer(cancelTransferReducer, initialState);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const responseError = useFormattedCombinedError(result.error);

  useEffect(() => {
    if (responseError) {
      dispatch({
        type: 'ERROR',
        errorScreen: {
          screenType: ErrorScreenType.Error,
          messageHeadline: responseError.title,
          messageBody: responseError.message,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.error]);

  const cancelTabapayTransfer = async (
    input: CancelTabapayTransferInput,
    flowName?: string,
  ): Promise<void> => {
    try {
      dispatch({ type: 'LOADING' });
      const result = await executeMutation({
        input: input,
      });

      const errorScreen = getTabapayErrorScreen(
        result?.data?.cancelTabapayTransfer,
      );
      if (errorScreen) {
        dispatch({ type: 'ERROR', errorScreen });
        return;
      }

      if (
        result?.data?.cancelTabapayTransfer.__typename ===
        CancelTabapayTransferResultTypenames.CancelTabapayTransferError
      ) {
        logTransactionAuthEvent(
          'Cancel Transaction Auth',
          'Generic_Error',
          segmentClient,
          flowName,
        );
        dispatch({
          type: 'ERROR',
          errorScreen: {
            screenType: ErrorScreenType.Error,
            messageHeadline: result.data.cancelTabapayTransfer.title,
            messageBody: result.data.cancelTabapayTransfer.message,
          },
        });
        return;
      }

      if (
        result?.data?.cancelTabapayTransfer.__typename ===
        CancelTabapayTransferResultTypenames.CancelTabapayTransferSuccess
      ) {
        logTransactionAuthEvent(
          'Cancel Transaction Auth',
          'Success',
          segmentClient,
          flowName,
        );
        if (result.data.cancelTabapayTransfer.success) {
          dispatch({ type: 'SUCCESS' });
          onSuccess?.();
        }
      }
    } catch (error) {
      let errorTitle: string = defaultTitle;
      let errorMessage: string = defaultMessage;

      if (isCoreError(error)) {
        errorTitle = error.title;
        errorMessage = error.message;
      } else if (isCombinedError(error)) {
        errorMessage = error.message;
      }

      dispatch({
        type: 'ERROR',
        errorScreen: {
          screenType: ErrorScreenType.Error,
          messageHeadline: errorTitle,
          messageBody: errorMessage,
        },
      });
    }
  };
  return {
    cancelTabapayTransfer,
    isSuccess: state.isSuccess,
    isLoading: state.isLoading,
    errorScreen: state.errorScreen,
  };
};

export default useCancelTabapayTransfer;
